import { useState, useCallback, useEffect, useMemo } from 'react';
import { Form, Field, FieldProps, FormikProvider, useFormik } from 'formik';
import { useSelector, useDispatch } from 'react-redux';
import { type AppState, type AppDispatch, Logger } from '@nextgen-web-framework/core';
import { useTranslation, Input, InputTypes, AccountType, MLPVersion } from '@nextgen-web-framework/all';
import {
  DynamicObjectInterface,
  updateRegistrationData,
  updateRegistrationSelectSponsorData,
} from '@commerce-profile-webcomponents/sdk';

import { inputErrorMessage, inputErrorStatus } from '../Common/InputErrorComponent/utils';
import {
  ApiStatus,
  ERROR_GENERIC_MESSAGE,
  ErrorConstants,
  TextConstants,
  REG_FLOW_WISE_FIELD,
} from '../../utils/Constants';
import { APICallType, IFieldVisibility } from '../Registration/types/propTypes';
import { useCountryMasterData, useRegistrationFieldsMasterData } from '../../utils/masterdata';

import {
  getFieldVisibility,
  getRegistrationValidationData,
  registrationValidationSchemafn,
  sponsorFailedHandler,
} from '../../utils/registrationUtils';
import { RegistrationValidations } from '../RegistrationIdVerificationStep/types/propTypes';
import ApiErrorModal from '../ApiErrorModal/ApiErrorModal';
import { ApiErrorModalProps } from '../ApiErrorModal/types/propTypes';
import RegistrationSponsor from '../RegistrationSponsor/RegistrationSponsor';
import { DoNotKnowSponsorFlow } from '../RegistrationSponsor/types/propTypes';
import PersonalIdAndTypeInput from '../PersonalIdAndTypeInput/PersonalIdAndTypeInput';
import DropDownwithMasterData from '../DropDownwithMasterData/DropDownwithMasterData';
import RegistrationUsername from '../RegistrationUsername/RegistrationUsername';
import RegistrationBirthDate from '../RegistrationBirthDate/RegistrationBirthDate';
import TermsAndConditionCheckBox from '../Common/TermsAndConditionCheckBox/TermsAndConditionCheckBox';
import { DropDownSelectType } from '../DropDownwithMasterData/types/propTypes';
import { RegistrationOpCenterDetailsStep } from './types/propTypes';
import styles from './RegistrationOpCenterStep.module.scss';

const RegistrationOpCenterDetailsStep = ({ nextHandler, setSubmitHandler }: RegistrationOpCenterDetailsStep) => {
  const logger = Logger.getInstance();
  const t = useTranslation();
  const dispatch = useDispatch<AppDispatch>();
  const { currentCountry } = useCountryMasterData();
  const registrationFlowData = useSelector((state: AppState) => state.commerceProfile.registrationFlowData);
  const selectedAccountType: AccountType = useSelector((state: AppState) => state.commerceProfile.selectedAccountType);
  const termsNConditionsCmsData = useSelector((state: AppState) => state.commerceProfile.termsNConditionsCMS);
  const termsAndConditions = termsNConditionsCmsData?.[selectedAccountType]?.termsAndConditions;
  const { perosonalDetails, userData, flowName, currentIndex, selectSponsor } = registrationFlowData;
  const { sponsor } = selectSponsor;

  const { patchVersion } = userData;
  const [modalErrorData, setModalErrorData] = useState<ApiErrorModalProps>();
  const [leadSearchEnabled, setLeadSearchEnabled] = useState(false);
  const registrationFieldsMasterData = useRegistrationFieldsMasterData();

  const [isPhonePrimaryContact, setIsPhonePrimaryContact] = useState<boolean>();
  // id api verify tracker
  const [sponsorFailed, setSponsorFailed] = useState<boolean>(false);

  //validation schema from master data

  const validationfields = useMemo(
    () =>
      getRegistrationValidationData({
        config: registrationFieldsMasterData as RegistrationValidations[],
        fieldNames: REG_FLOW_WISE_FIELD.opsLedFlow,
      }),
    [],
  );
  const validationSchema = () => registrationValidationSchemafn(validationfields, selectedAccountType);

  const fieldVisibilityDictionary: IFieldVisibility = useMemo(() => {
    let data: IFieldVisibility = {};
    if (registrationFieldsMasterData?.length) {
      data = getFieldVisibility(registrationFieldsMasterData, 'pk', selectedAccountType, flowName);
    }
    return data;
  }, [selectedAccountType]);

  const formik = useFormik({
    initialValues: perosonalDetails,
    onSubmit: () => submitRegistrationdata(),
    validationSchema: validationSchema,
    validateOnBlur: true,
    initialStatus: { enableField: AccountType.Employee === selectedAccountType },
  });
  logger.log(formik.isValid, 'formik.isValid', formik.errors, formik?.status?.enableField, formik.values);

  const { values, handleChange, handleBlur, setFieldValue, resetForm, status, handleSubmit, isValid, errors } = formik;
  const formikErrorLength = Object.keys(errors).length == 0;
  useEffect(() => {
    const isFormValid = formik.isValid && Object.keys(formik.errors).length == 0 && values.firstName;
    if (setSubmitHandler && isFormValid) {
      setSubmitHandler(() => handleSubmit);
    } else if (setSubmitHandler) {
      setSubmitHandler(() => undefined);
    }
  }, [setSubmitHandler, handleSubmit, isValid, formikErrorLength, values.firstName]);
  useEffect(() => {
    if (fieldVisibilityDictionary?.['sponsor']) {
      setFieldValue('sponsor', sponsor?.accountNumber);
    }
  }, [sponsor?.accountNumber]);

  const submitRegistrationdata = async () => {
    const payload: DynamicObjectInterface = {
      givenName: values.firstName,
      familyName: values.lastName,
      version: patchVersion,
      doNotKnowSponsor: leadSearchEnabled,
    };
    if (values.gender) {
      payload.gender = values.gender;
      const { birthDate } = values; // DD/MM/YYYY
      const [day, month, year] = birthDate.split('/');

      const formattedBirthdate = `${year}-${month}-${day}`;
      payload.birthDate = formattedBirthdate;
      payload.employeeId = values.employeeId;
      payload.email = values.email;
    } else {
      payload.taxRegime = values.taxRegime;
    }
    if (sponsor) {
      payload.sponsor = sponsor.accountNumber;
    }
    if (values.citizenshipCountry) {
      payload.citizenshipCountry = values.citizenshipCountry;
      //current logic without citizenship selection on UI.
      if (values.citizenshipCountry == currentCountry?.pk) {
        payload.citizenship = 'CITIZEN';
      } else {
        payload.citizenship = 'FOREIGNER';
      }
    }
    const callOnSuccess = true;
    const res = await Promise.resolve(nextHandler(payload, undefined, false, callOnSuccess));
    if (res?.responseBody) {
      const { code, status } = res.responseBody;
      errorHandler(code, status);
    } else {
      resetPersonalForm();
      setFieldValue('termsAndConditionCheckbox', false);
      dispatch(
        updateRegistrationSelectSponsorData({
          sponsor: {},
          knowABO: true,
          sponsorSearchText: '',
          isInternational: false,
          internationalSponsor: {},
          internationalSponsorCountryCode: '',
          internationalSponsorTncCheckbox: false,
          internationalSposnorVerified: false,
          editSponsor: true,
          setInternationalSponsor: false,
          hideSponsorRadioSelection: false,
        }),
      );
    }
  };

  const cidExpiredHandler = () => {
    dispatch(
      updateRegistrationData({
        currentIndex: currentIndex - 1,
      }),
    );
  };

  const handleSponsorFailedAndUpdateState = () =>
    sponsorFailedHandler(flowName, () => {
      dispatch(
        updateRegistrationSelectSponsorData({
          sponsor: {},
          editSponsor: true,
        }),
      );
      setSponsorFailed(true);
    });

  const errorHandler = (code: string, status: number) => {
    if (status === 400) {
      if (code.includes(ApiStatus.BAD_REQUEST)) {
        setModalErrorData({
          headline: ERROR_GENERIC_MESSAGE.headline,
          body: ERROR_GENERIC_MESSAGE.body,
        });
      } else {
        const errorCallback = () => {
          dispatch(updateRegistrationData({}));
        };
        let errorModalCallback = errorCallback;
        let buttonLabel = undefined;
        if (code === ErrorConstants.SPONSOR_FAILED) {
          buttonLabel = `profile.ApiErrorModal.${code}.button`;
          errorModalCallback = handleSponsorFailedAndUpdateState;
        } else if (code === ErrorConstants.CID_EXPIRED_CODE) {
          buttonLabel = `profile.ApiErrorModal.${code}.button`;
          errorModalCallback = cidExpiredHandler;
        }
        setModalErrorData({
          headline: `profile.ApiErrorModal.${code}.headline`,
          body: `profile.ApiErrorModal.${code}.body`,
          primaryButtonCallback: errorModalCallback,
          primaryButtonLabel: buttonLabel ?? '',
        });
      }
    } else {
      setModalErrorData({
        headline: ERROR_GENERIC_MESSAGE.headline,
        body: ERROR_GENERIC_MESSAGE.body,
      });
    }
  };

  const resetPersonalForm = useCallback(() => {
    resetForm({});
    setFieldValue('birthDate', '');
    setFieldValue('employeeId', '');
  }, [resetForm]);
  const nextHandlerCallback = useCallback(
    async (payload: DynamicObjectInterface) => {
      const response = await nextHandler(payload, undefined, true, false, APICallType.PATCH);
      return response;
    },
    [nextHandler],
  );

  return (
    <div
      className={styles['registration-personal-details-wrapper']}
      data-testid="registration-personal-details-wrapper"
    >
      <div
        className={styles['registration-personal-details-conatiner']}
        data-testid="registration-personal-details-conatiner"
      >
        <FormikProvider value={formik}>
          <Form>
            <div className={styles['input-form']} data-testid="input-form">
              <div className={`${styles['input-form-wrapper']}`} data-testid="input-form-wrapper">
                <div className={styles['info-section']}>
                  <div
                    className={`${styles['input-wrapper']} ${
                      !fieldVisibilityDictionary?.['personalIdType'] ||
                      !fieldVisibilityDictionary?.['personalIdRecordNumber']
                        ? styles['input-hide']
                        : ''
                    }`}
                  >
                    <PersonalIdAndTypeInput
                      nextHandlerCallback={nextHandlerCallback}
                      resetPersonalForm={resetPersonalForm}
                      isRefreshRestoredData={false}
                      mlpVersionProp={MLPVersion.ONE}
                    />
                  </div>

                  <div className={styles['input-wrapper']} data-testid="name-wrapper">
                    <div className={styles['input-fields']} data-testid="input-fname-field">
                      <Field type="text" name="firstName">
                        {({ meta }: FieldProps) => (
                          <Input
                            id="firstName"
                            data-testid="firstName"
                            type={InputTypes.TEXT}
                            name="firstName"
                            value={values.firstName}
                            label={t('profile.RegistrationPersonalDetailsStep.firstName')}
                            onChange={handleChange}
                            onBlur={handleBlur}
                            mlpVersion={MLPVersion.ONE}
                            status={inputErrorStatus(meta.touched, meta.error)}
                            message={inputErrorMessage(t, meta.touched, meta.error && `${meta.error}.opsCenter`, {
                              max: TextConstants.NAME_MAX,
                            })}
                            disabled={!status?.enableField}
                          />
                        )}
                      </Field>
                    </div>

                    <div className={styles['input-fields']}>
                      <Field type="text" name="lastName">
                        {({ meta }: FieldProps) => (
                          <Input
                            id="lastName"
                            data-testid="lastName"
                            type={InputTypes.TEXT}
                            name="lastName"
                            label={t('profile.RegistrationPersonalDetailsStep.lastName')}
                            onChange={handleChange}
                            value={values.lastName}
                            onBlur={handleBlur}
                            mlpVersion={MLPVersion.ONE}
                            status={inputErrorStatus(meta.touched, meta.error)}
                            message={inputErrorMessage(t, meta.touched, meta.error && `${meta.error}.opsCenter`, {
                              max: TextConstants.NAME_MAX,
                            })}
                            disabled={!status?.enableField}
                          />
                        )}
                      </Field>
                    </div>
                  </div>

                  <div
                    className={`${styles['input-wrapper']} ${
                      !fieldVisibilityDictionary?.['gender'] && !fieldVisibilityDictionary?.['birthDate']
                        ? styles['input-hide']
                        : ''
                    }`}
                  >
                    <div className={`${styles['input-fields']}`}>
                      <RegistrationBirthDate
                        name="birthDate"
                        selectedAccountType={selectedAccountType}
                        isLunar={false}
                        dateFormat={'DD/MM/YYYY'}
                        mlpVersionProp={MLPVersion.ONE}
                      />
                    </div>

                    <div className={`${styles['input-fields']}`}>
                      <Field type="text" name="gender">
                        {() => (
                          <DropDownwithMasterData
                            fieldName="gender"
                            value={values.gender}
                            onSelect={(val) => setFieldValue('gender', val)}
                            disabled={!status?.enableField}
                            mlpVersionProp={MLPVersion.ONE}
                            dropDownSelectType={DropDownSelectType.DROPDOWN}
                          />
                        )}
                      </Field>
                    </div>
                  </div>

                  <div className={styles['input-wrapper']}>
                    <div
                      className={`${styles['input-fields']} ${
                        !fieldVisibilityDictionary?.['taxRegime'] ? styles['input-hide'] : ''
                      }`}
                    >
                      <Field type="text" name="taxRegime">
                        {() => (
                          <DropDownwithMasterData
                            fieldName="taxRegime"
                            value={values.taxRegime}
                            onSelect={(key) => setFieldValue('taxRegime', key)}
                            mlpVersionProp={MLPVersion.ONE}
                            dropDownSelectType={DropDownSelectType.DROPDOWN}
                            disabled={!status?.enableField}
                          />
                        )}
                      </Field>
                    </div>

                    <div
                      className={`${styles['input-fields']} ${
                        !fieldVisibilityDictionary?.['employeeId'] ? styles['input-hide'] : ''
                      }`}
                    >
                      <Field type="text" name="employeeId">
                        {({ meta }: FieldProps) => (
                          <Input
                            id="employeeId"
                            data-testid="employeeId"
                            type={InputTypes.TEXT}
                            name="employeeId"
                            label={t('profile.RegistrationOpsLedStep.employeeId')}
                            onChange={handleChange}
                            value={values.employeeId}
                            onBlur={handleBlur}
                            mlpVersion={MLPVersion.ONE}
                            status={inputErrorStatus(meta.touched, meta.error)}
                            message={inputErrorMessage(t, meta.touched, meta.error, {
                              max: TextConstants.NAME_MAX,
                            })}
                            disabled={!status?.enableField}
                          />
                        )}
                      </Field>
                    </div>

                    <div className={`${styles['input-fields']}`}>
                      <Field type="text" name="citizenshipCountry">
                        {() => (
                          <DropDownwithMasterData
                            fieldName="citizenshipCountry"
                            value={values.citizenshipCountry}
                            onSelect={(key) => setFieldValue('citizenshipCountry', key)}
                            translationPrefix="profile.RegistrationIdVerificationStep.country"
                            mlpVersionProp={MLPVersion.ONE}
                            dropDownSelectType={DropDownSelectType.DROPDOWN}
                            disabled={!status?.enableField}
                          />
                        )}
                      </Field>
                    </div>
                  </div>

                  <div
                    className={`${styles['input-wrapper']} ${
                      !fieldVisibilityDictionary?.['employeeId'] ? styles['input-hide'] : ''
                    }`}
                  >
                    <div className={`${styles['input-fields']}`}>
                      <Field type="email" name="email">
                        {({ meta }: FieldProps) => (
                          <Input
                            id="email"
                            data-testid="email"
                            type={InputTypes.EMAIL}
                            name="email"
                            label={t('profile.RegistrationUsersDetailsStep.email')}
                            onChange={handleChange}
                            value={values.email}
                            onBlur={handleBlur}
                            mlpVersion={MLPVersion.ONE}
                            status={inputErrorStatus(meta.touched, meta.error)}
                            message={inputErrorMessage(t, meta.touched, meta.error, {
                              max: TextConstants.NAME_MAX,
                            })}
                            disabled={!status?.enableField}
                          />
                        )}
                      </Field>

                      <span className={styles.inputInfo}>{t('profile.RegistrationOpsLedStep.emailInfo')}</span>
                    </div>
                  </div>
                </div>

                <div className={`${!fieldVisibilityDictionary?.['sponsor'] ? styles['input-hide'] : ''}`}>
                  <RegistrationUsername
                    isPhonePrimaryContact={isPhonePrimaryContact}
                    setIsPhonePrimaryContact={setIsPhonePrimaryContact}
                    showPasswordField={false}
                    nextHandler={nextHandler}
                  />
                </div>
              </div>

              {/* ---------sponsor-container-------- */}
              <div
                className={`${styles['info-section']} ${
                  !fieldVisibilityDictionary?.['sponsor'] ? styles['input-hide'] : ''
                }`}
              >
                <div className={styles['info-sub-section']}>
                  <div className={styles['id-section-label']}>
                    {t('profile.RegistrationUsersDetailsStep.sponsorLabel')}
                  </div>
                  <div className={styles['input-wrapper']}>
                    <div className={styles['input-fields']}>
                      <Field
                        component={RegistrationSponsor}
                        name="sponsor"
                        isFieldDisabled={false}
                        showABOSelected={false}
                        isFocused={sponsorFailed}
                        wrapperClass={styles['sponsor-details']}
                        doNotKnowAboFlowType={DoNotKnowSponsorFlow.WITHOUT_POSTAL_CODE_SEARCH}
                        onLeadsSearchHandler={(value: boolean) => setLeadSearchEnabled(value)}
                      />
                    </div>
                  </div>
                </div>
                <div className={`${styles['info-section']} ${styles['remove-border']}`}>
                  <div className={styles['info-sub-section']}>
                    <Field type="checkbox" name="termsAndConditionCheckbox">
                      {() => (
                        <TermsAndConditionCheckBox
                          tncText={t('profile.RegistrationIdVerificationStep.checkboxLabel')}
                          tncLinkText={t('profile.RegistrationIdVerificationStep.TNCcheckbox')}
                          tncContent={termsAndConditions}
                          disabled={!formik.status?.enableField}
                          onCheckBoxClick={(val) => {
                            setFieldValue('termsAndConditionCheckbox', !!val);
                          }}
                          checked={formik.values.termsAndConditionCheckbox}
                        />
                      )}
                    </Field>
                  </div>
                </div>
              </div>
            </div>
          </Form>
        </FormikProvider>
      </div>

      {(modalErrorData?.headline || modalErrorData?.body) && (
        <ApiErrorModal {...modalErrorData} primaryButtonCallback={() => setModalErrorData(undefined)} />
      )}
    </div>
  );
};
export default RegistrationOpCenterDetailsStep;
